import { reactive, computed, readonly } from 'vue';

const wizardState = reactive({
  currentStep: 1,

  // Step 1: Audience
  audienceMode: null, // 'labels' | 'spreadsheet' | 'slash_command'
  selectedLabels: [],
  uploadedContacts: [],
  contactHeaders: [],
  uploadWarnings: {},
  phoneColumn: null,
  nameColumn: null,

  // GK-HUB: N8N segment state
  n8nConfigured: false,
  n8nSegments: [],
  selectedSegmentId: null,
  segmentParameters: {},
  segmentContacts: [],
  segmentHeaders: [],
  isQueryingSegment: false,
  segmentError: null,
  customEndpoint: '',
  // GK-HUB: end N8N segment state

  // Step 2: Template
  inboxId: null,
  templateId: null,
  selectedTemplate: null,
  variableMapping: {},

  // Step 3: Review
  title: '',
  scheduledAt: null,
  teamId: null, // GK-HUB: Team assignment for campaign conversations
  assigneeId: null, // GK-HUB: Optional agent assignee (filtered by teamId)

  // GK-HUB: R6 — Throttle + auto-retry config
  throttleIntervalMs: 0,
  maxRetries: 0,
});

const PHONE_HINTS = [
  'telefone',
  'celular',
  'phone',
  'fone',
  'whatsapp',
  'numero',
  'tel',
  'mobile',
];

const NAME_HINTS = ['nome', 'name', 'cliente', 'customer', 'contato'];

const availableFields = computed(() => {
  if (wizardState.audienceMode === 'spreadsheet') {
    return wizardState.contactHeaders;
  }
  // GK-HUB: N8N segment fields
  if (wizardState.audienceMode === 'slash_command') {
    return wizardState.segmentHeaders;
  }
  return ['nome', 'telefone', 'email'];
});

const contactCount = computed(() => {
  if (wizardState.audienceMode === 'spreadsheet') {
    return wizardState.uploadedContacts.length;
  }
  // GK-HUB: N8N segment count
  if (wizardState.audienceMode === 'slash_command') {
    return wizardState.segmentContacts.length;
  }
  return null;
});

const firstContact = computed(() => {
  if (
    wizardState.audienceMode === 'spreadsheet' &&
    wizardState.uploadedContacts.length > 0
  ) {
    return wizardState.uploadedContacts[0];
  }
  // GK-HUB: N8N segment first contact
  if (
    wizardState.audienceMode === 'slash_command' &&
    wizardState.segmentContacts.length > 0
  ) {
    return wizardState.segmentContacts[0];
  }
  return null;
});

const canProceedStep1 = computed(() => {
  if (wizardState.audienceMode === 'spreadsheet') {
    return wizardState.uploadedContacts.length > 0;
  }
  if (wizardState.audienceMode === 'labels') {
    return wizardState.selectedLabels.length > 0;
  }
  // GK-HUB: N8N segment proceed condition
  if (wizardState.audienceMode === 'slash_command') {
    return wizardState.segmentContacts.length > 0;
  }
  return false;
});

const canProceedStep2 = computed(() => {
  return !!(wizardState.inboxId && wizardState.templateId);
});

const canSubmit = computed(() => {
  return !!(wizardState.title && wizardState.title.trim().length > 0);
});

function autoDetectColumns(headers) {
  const lower = headers.map(h => h.toLowerCase().trim());

  const phoneIdx = lower.findIndex(h =>
    PHONE_HINTS.some(hint => h.includes(hint))
  );
  if (phoneIdx >= 0) {
    wizardState.phoneColumn = headers[phoneIdx];
  }

  const nameIdx = lower.findIndex(h =>
    NAME_HINTS.some(hint => h.includes(hint))
  );
  if (nameIdx >= 0) {
    wizardState.nameColumn = headers[nameIdx];
  }
}

function goToStep(step) {
  if (step >= 1 && step <= 3) {
    wizardState.currentStep = step;
  }
}

function nextStep() {
  if (wizardState.currentStep < 3) {
    wizardState.currentStep += 1;
  }
}

function prevStep() {
  if (wizardState.currentStep > 1) {
    wizardState.currentStep -= 1;
  }
}

function resetWizard() {
  Object.assign(wizardState, {
    currentStep: 1,
    audienceMode: null,
    selectedLabels: [],
    uploadedContacts: [],
    contactHeaders: [],
    uploadWarnings: {},
    phoneColumn: null,
    nameColumn: null,
    // GK-HUB: reset N8N segment state
    n8nConfigured: false,
    n8nSegments: [],
    selectedSegmentId: null,
    segmentParameters: {},
    segmentContacts: [],
    segmentHeaders: [],
    isQueryingSegment: false,
    segmentError: null,
    customEndpoint: '',
    // GK-HUB: end reset N8N segment state
    inboxId: null,
    templateId: null,
    selectedTemplate: null,
    variableMapping: {},
    title: '',
    scheduledAt: null,
    teamId: null, // GK-HUB: reset team assignment
    assigneeId: null, // GK-HUB: reset assignee
    // GK-HUB: R6 — reset throttle + retry
    throttleIntervalMs: 0,
    maxRetries: 0,
  });
  // GK-HUB: clear prefill hints
  // eslint-disable-next-line no-underscore-dangle
  delete wizardState._prefillTemplateName;
  // eslint-disable-next-line no-underscore-dangle
  delete wizardState._prefillTemplateLanguage;
}

// GK-HUB: Prefill wizard from existing campaign (for duplicate)
function prefillFromCampaign(campaign) {
  resetWizard();
  if (!campaign) return;

  // Basic fields
  wizardState.title = `${campaign.title || ''} (Cópia)`;
  wizardState.inboxId = campaign.inbox?.id || campaign.inbox_id || null;
  wizardState.teamId = campaign.team?.id || campaign.team_id || null;
  // GK-HUB: prefill assignee from campaign
  wizardState.assigneeId =
    campaign.assignee?.id || campaign.assignee_id || null;

  // Audience
  const aType = campaign.audience_type;
  if (aType === 'labels' || aType === 0) {
    wizardState.audienceMode = 'labels';
    wizardState.selectedLabels = (campaign.audience || []).map(a => a.id);
  } else if (
    aType === 'spreadsheet' ||
    aType === 1 ||
    aType === 'slash_command' ||
    aType === 2
  ) {
    // slash_command duplicates as spreadsheet (snapshot of original contacts)
    wizardState.audienceMode = 'spreadsheet';
    wizardState.uploadedContacts = campaign.audience_contacts || [];
    if (wizardState.uploadedContacts.length > 0) {
      wizardState.contactHeaders = Object.keys(wizardState.uploadedContacts[0]);
      autoDetectColumns(wizardState.contactHeaders);
    }
  }

  // Variable mapping
  if (
    campaign.variable_mapping &&
    Object.keys(campaign.variable_mapping).length > 0
  ) {
    wizardState.variableMapping = { ...campaign.variable_mapping };
  }

  // GK-HUB: R6 — Copy throttle + retry config (but reset retry_count)
  if (campaign.throttle_interval_ms != null) {
    wizardState.throttleIntervalMs = campaign.throttle_interval_ms;
  }
  if (campaign.max_retries != null) {
    wizardState.maxRetries = campaign.max_retries;
  }

  // Template auto-select hint (consumed by StepTemplateMapping watcher)
  if (campaign.template_params?.name) {
    // eslint-disable-next-line no-underscore-dangle
    wizardState._prefillTemplateName = campaign.template_params.name;
    // eslint-disable-next-line no-underscore-dangle
    wizardState._prefillTemplateLanguage =
      campaign.template_params.language || 'en_US';
  }
}
// GK-HUB: end prefill

export function useCampaignWizard() {
  return {
    wizardState,
    availableFields: readonly(availableFields),
    contactCount: readonly(contactCount),
    firstContact: readonly(firstContact),
    canProceedStep1: readonly(canProceedStep1),
    canProceedStep2: readonly(canProceedStep2),
    canSubmit: readonly(canSubmit),
    autoDetectColumns,
    goToStep,
    nextStep,
    prevStep,
    prefillFromCampaign, // GK-HUB: duplicate campaign
    resetWizard,
  };
}
